export type PlanningTaskStatusId =
  | 'Planned'
  | 'InProgress'
  | 'Completed'
  | 'Cancelled'
  | 'Overdue'
  | 'NotStarted'
  | 'Abandoned';

export type PlanningTaskStatusConfig = {
  id: PlanningTaskStatusId;
  label: string;
  headerClass: string;
  columnClass: string;
  cardAccentClass: string;
};

export const PLANNING_TASK_STATUSES: PlanningTaskStatusConfig[] = [
  {
    id: 'Planned',
    label: 'Planejada',
    headerClass: 'bg-slate-600 text-white',
    columnClass: 'bg-slate-50 border-slate-200',
    cardAccentClass: 'border-l-slate-500',
  },
  {
    id: 'InProgress',
    label: 'Em andamento',
    headerClass: 'bg-sky-600 text-white',
    columnClass: 'bg-sky-50 border-sky-200',
    cardAccentClass: 'border-l-sky-500',
  },
  {
    id: 'Overdue',
    label: 'Atrasada',
    headerClass: 'bg-amber-600 text-white',
    columnClass: 'bg-amber-50 border-amber-200',
    cardAccentClass: 'border-l-amber-500',
  },
  {
    id: 'Completed',
    label: 'Concluída',
    headerClass: 'bg-emerald-600 text-white',
    columnClass: 'bg-emerald-50 border-emerald-200',
    cardAccentClass: 'border-l-emerald-500',
  },
  {
    id: 'Cancelled',
    label: 'Cancelada',
    headerClass: 'bg-rose-600 text-white',
    columnClass: 'bg-rose-50 border-rose-200',
    cardAccentClass: 'border-l-rose-500',
  },
];

/** Status exclusivos de meta (não entram no kanban de tarefas). */
export const META_OUTCOME_STATUS_LABELS: Record<'NotStarted' | 'Abandoned', string> = {
  NotStarted: 'Não iniciada',
  Abandoned: 'Abandonada',
};

/** Status de desfecho de meta que exigem comentário de motivo. */
export const META_REASON_REQUIRED_STATUSES: PlanningTaskStatusId[] = [
  'Cancelled',
  'NotStarted',
  'Abandoned',
];

export function requiresPlanningStatusReason(status: string): boolean {
  return META_REASON_REQUIRED_STATUSES.includes(status as PlanningTaskStatusId);
}

export function getPlanningStatusReasonCopy(status: string): {
  label: string;
  placeholder: string;
} {
  switch (status) {
    case 'NotStarted':
      return {
        label: 'Motivo de não iniciada',
        placeholder: 'Explique por que nenhuma ação foi iniciada nesta meta.',
      };
    case 'Abandoned':
      return {
        label: 'Motivo do abandono',
        placeholder: 'Explique por que a execução não pôde ser concluída.',
      };
    default:
      return {
        label: 'Motivo do cancelamento',
        placeholder: 'Descreva por que este item está sendo cancelado.',
      };
  }
}

export function getPlanningTaskStatusConfig(status: string): PlanningTaskStatusConfig {
  return PLANNING_TASK_STATUSES.find((item) => item.id === status) ?? PLANNING_TASK_STATUSES[0];
}

export function formatPlanningTaskPriority(priority: string): string {
  switch (priority) {
    case 'Low':
      return 'Baixa';
    case 'High':
      return 'Alto';
    case 'Critical':
      return 'Crítica';
    default:
      return 'Média';
  }
}

export function formatPlanningCycleKind(kind: string): string {
  switch (kind) {
    case 'Annual':
      return 'Anual';
    case 'Quarterly':
      return 'Trimestral';
    case 'Sprint':
      return 'Sprint';
    case 'Project':
      return 'Projeto';
    case 'Commercial':
      return 'Comercial';
    case 'MpsBrLevelC':
      return 'MPS.Br Nível C';
    case 'Custom':
      return 'Personalizado';
    default:
      return kind;
  }
}

export function formatPlanningCycleStatus(status: string): string {
  switch (status) {
    case 'Active':
      return 'Ativo';
    case 'Closed':
      return 'Encerrado';
    case 'Cancelled':
      return 'Cancelado';
    default:
      return 'Rascunho';
  }
}

export function formatPlanningTaskStatusLabel(status: string): string {
  if (status === 'NotStarted' || status === 'Abandoned') {
    return META_OUTCOME_STATUS_LABELS[status];
  }
  return getPlanningTaskStatusConfig(status as PlanningTaskStatusId).label;
}

export function canEditPlanningTaskOwner(
  status: string,
  executionFinishedAt?: string | null,
): boolean {
  if (executionFinishedAt) return false;
  if (
    status === 'Completed'
    || status === 'Cancelled'
    || status === 'NotStarted'
    || status === 'Abandoned'
  ) {
    return false;
  }
  return status === 'Planned' || status === 'InProgress' || status === 'Overdue';
}

type PlanningResultOption = {
  value: string;
  label: string;
};

export const PLANNING_FULFILLMENT_OPTIONS: PlanningResultOption[] = [
  { value: 'NotFulfilled', label: 'Não realizada' },
  { value: 'PartiallyFulfilled', label: 'Parcialmente realizada' },
  { value: 'Fulfilled', label: 'Realizada' },
  { value: 'Cancelled', label: 'Cancelada' },
];

export const PLANNING_EVALUATION_RESULT_OPTIONS: PlanningResultOption[] = [
  { value: 'NotExecuted', label: 'Não cumpriu' },
  { value: 'PartiallyExecuted', label: 'Cumpriu parcialmente' },
  { value: 'Executed', label: 'Cumpriu' },
  { value: 'Cancelled', label: 'Cancelada' },
];

const PLANNING_FULFILLMENT_LABELS: Record<string, string> = {
  notfulfilled: 'Não realizada',
  partiallyfulfilled: 'Parcialmente realizada',
  fulfilled: 'Realizada',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  '1': 'Não realizada',
  '2': 'Parcialmente realizada',
  '3': 'Realizada',
  '4': 'Cancelada',
};

const PLANNING_EVALUATION_RESULT_LABELS: Record<string, string> = {
  notexecuted: 'Não cumpriu',
  partiallyexecuted: 'Cumpriu parcialmente',
  executed: 'Cumpriu',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  '1': 'Não cumpriu',
  '2': 'Cumpriu parcialmente',
  '3': 'Cumpriu',
  '4': 'Cancelada',
};

function normalizePlanningResultKey(result?: string | number | null): string {
  if (result === null || result === undefined) return '';
  return String(result).trim().toLowerCase().replace(/[\s_-]+/g, '');
}

export function formatPlanningFulfillmentResult(result?: string | number | null): string {
  const key = normalizePlanningResultKey(result);
  if (!key) return '—';
  return PLANNING_FULFILLMENT_LABELS[key] ?? String(result).trim();
}

export function formatPlanningDeliverableResult(result?: string | number | null): string {
  const key = normalizePlanningResultKey(result);
  if (!key) return '—';
  return PLANNING_EVALUATION_RESULT_LABELS[key] ?? String(result).trim();
}

export function formatPlanningEvaluationQuality(value?: string | number | null): string {
  switch (normalizePlanningResultKey(value)) {
    case 'excellent':
    case '1':
      return 'Excelente';
    case 'good':
    case '2':
      return 'Boa';
    case 'fair':
    case '3':
      return 'Razoável';
    case 'unsatisfactory':
    case '4':
      return 'Insatisfatória';
    default:
      return value == null || String(value).trim() === '' ? '—' : String(value).trim();
  }
}

export function formatPlanningEvaluationDeadline(value?: string | number | null): string {
  switch (normalizePlanningResultKey(value)) {
    case 'early':
    case '1':
      return 'Adiantada';
    case 'ontime':
    case '2':
      return 'No prazo';
    case 'late':
    case '3':
      return 'Atrasada';
    default:
      return value == null || String(value).trim() === '' ? '—' : String(value).trim();
  }
}

export function formatPlanningEvaluationImpact(value?: string | number | null): string {
  switch (normalizePlanningResultKey(value)) {
    case 'low':
    case '1':
      return 'Baixo';
    case 'medium':
    case '2':
      return 'Médio';
    case 'high':
    case '3':
      return 'Alto';
    case 'strategic':
    case '4':
      return 'Estratégico';
    default:
      return value == null || String(value).trim() === '' ? '—' : String(value).trim();
  }
}
